import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { EOrgType } from '@unfoldrtech/portal-mic'
import {
  EChannelTypeGlobal,
  EParamChannelTypeGlobal,
  EReportColumnHeader,
  EReportingTimeWindow,
  IDAReportCampaignsTable,
  IDOOHReportCampaignsTable,
  IGenericReportCampaignsTable,
  ISPReportCampaignsTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { ReportingCampaignsChartWrapper } from './ReportingCampaignsChartWrapper'
import { TReportExportType, TReportingFilters } from '../../../../models'
import { AppContext } from '../../../../models/contexts'
import {
  selectCampaignsTableReportingFilters,
  selectReportingFilters,
  setIsReportingV3,
  setStatus,
} from '../../../../store/reportingFilters'
import { setReportingData } from '../../../../store/reportingData'
import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import { ReportingCampaignsTableWrapper } from './ReportingCampaignsTableWrapper'
import {
  convertEParamChannelTypeToEChannelTypeGlobal,
  generateCorrelationId,
  getTimezone,
} from '../../../../utils/helpers'
import { sanitizeDataTestId } from '../../../../utils'
import { getCampaignsEDatasetKeyFromChannelTypeGlobal } from '../helpers/get-campaigns-e-dataset-key-from-channel-type-global'
import { TDatasetWhitelistKey } from '../../models'
import { coreAPIV3 } from '../../../../services/coreAPIV3'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'
import { getDefaultStatusesForDataset } from '../../../../hooks/use-get-reporting-filter-options'

export const ReportingCampaignsTemplate = ({
  channelType: propChannelType,
  isHome,
}: {
  channelType?: EChannelTypeGlobal
  isHome?: boolean
}) => {
  let pageTitle = propChannelType
    ? `reporting.header.campaigns.${sanitizeDataTestId(propChannelType)}`
    : 'reporting.header.campaigns'
  pageTitle = isHome ? 'home' : pageTitle

  const intl = useIntl()
  const dispatch = useDispatch()

  const [appContext] = useContext(AppContext)
  const filters = useSelector(selectCampaignsTableReportingFilters)
  const { channelType: reportingFilterChannelType } = useSelector(
    selectReportingFilters
  )
  const channelType =
    propChannelType ||
    convertEParamChannelTypeToEChannelTypeGlobal(reportingFilterChannelType)

  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()
  const orgType = appContext.userOrg!.type

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<
    | IGenericReportCampaignsTable
    | ISPReportCampaignsTable
    | IDAReportCampaignsTable
    | IDOOHReportCampaignsTable
  >()
  const [reportingFiltersIsSetup, setReportingFiltersIsSetup] = useState(false)

  const onTableDataChange = (
    campaignsTableData:
      | IGenericReportCampaignsTable
      | ISPReportCampaignsTable
      | IDAReportCampaignsTable
      | IDOOHReportCampaignsTable
  ) => {
    setTableData(campaignsTableData)
  }
  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) ||
      getCampaignsEDatasetKeyFromChannelTypeGlobal(channelType),
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res =
        orgType === EOrgType.Advertiser
          ? await coreAPIV3
              .advertiserReporting()
              .getAdvertiserCampaignsReportTableV3(
                advertiserId,
                generateCorrelationId(),
                retailerId,
                channelType as unknown as EParamChannelTypeGlobal,
                filters.sortBy as EReportColumnHeader,
                filters.sortOrder,
                filters.timeWindow as unknown as EReportingTimeWindow,
                start,
                end,
                filters.tzZone || getTimezone(),
                EXPORT_PAGE_LIMIT,
                0,
                filters.filterBy,
                filters.filterOperation,
                filters.filterValue,
                filters.name ? encodeURI(filters.name) : undefined,
                // @ts-expect-error: fix in PORT-2535
                filters.status
              )
          : await coreAPIV3
              .retailerReporting()
              .getRetailerCampaignsReportTableV3(
                retailerId,
                generateCorrelationId(),
                advertiserId,
                channelType as unknown as EParamChannelTypeGlobal,
                filters.sortBy as EReportColumnHeader,
                filters.sortOrder,
                filters.timeWindow as EReportingTimeWindow,
                start,
                end,
                filters.tzZone || getTimezone(),
                EXPORT_PAGE_LIMIT,
                0,
                filters.filterBy,
                filters.filterOperation,
                filters.filterValue,
                filters.name ? encodeURI(filters.name) : undefined,
                // @ts-expect-error: fix in PORT-2535
                filters.status
              )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  useEffect(() => {
    const datasetKey = getCampaignsEDatasetKeyFromChannelTypeGlobal(channelType)
    const defaultStatuses = getDefaultStatusesForDataset(datasetKey)

    dispatch(setReportingData({ datasetKey }))
    dispatch(setStatus({ status: defaultStatuses }))
    setReportingFiltersIsSetup(true)
  }, [channelType])

  return (
    <>
      {reportingFiltersIsSetup && (
        <ReportPageTemplateLayout
          title={pageTitle}
          isExportDisabled={isExporting || !tableData?.datasetKey}
          chartWrapper={
            <ReportingCampaignsChartWrapper channelType={channelType} />
          }
          tableWrapper={
            <ReportingCampaignsTableWrapper
              onTableDataChange={onTableDataChange}
              channelType={channelType}
              isHome={isHome}
            />
          }
          onExportData={onExportData}
        />
      )}
      <SuccessToast
        show={showExportCappedToast}
        onClose={() => setShowExportCappedToast(false)}
        translationKey="reporting.export.capped"
      />
      <ErrorToast
        show={showExportErrorToast}
        onClose={() => setShowExportErrorToast(false)}
        translationKey="reporting.export.failed"
      />
    </>
  )
}
